import { z } from 'zod';
import { RowVersionSchema } from '../common/row-version.js';
import { LanguageSchema } from '../common/language.js';
// Response schemas for the SYSADMIN platform surface (/api/platform/projects/*,
// ADR-018 Zone 2). Distinct from `responses.ts` (the member-read /api/projects/:id
// surface, 9 fields): this surface is the cross-project management console, so it
// exposes the full management field set + the derived stats (member / active-site
// counts) the list, peek, and detail render.
//
// Field-exposure (ANATOMY §field-exposure): the list item carries everything the
// list + peek show; the detail adds the heavier identity + defaults fields.
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
export const ProjectTypeSchema = z.enum(['esco', 'embedded', 'demo']);
export const ProjectStatusSchema = z.enum(['active', 'suspended', 'terminated']);
// One tier a project INCLUDES (ADR-055 — every type) with its licensing figures:
// the purchased seat cap of a prepaid (embedded/demo) project, or `null` for a
// subscription (esco — included without seats, metered) + live usage, resolved
// through `sites.site_type_id → site_types.license_tier_code`.
//
// `sites_held` counts SEATS HELD, which per ADR-055 is `active` OR `suspended`
// (suspend is an operational pause that keeps its seat; decommission frees it; a
// draft never holds one). That is deliberately NOT the same number as the
// `active_site_count` KPI on the project row, which counts `status='active'`
// only — so on a project with suspended sites the two legitimately differ.
export const ProjectTierSchema = z
    .object({
    tier_code: z.string().max(16),
    tier_name: z.string().max(32),
    max_active_power_systems: z.number().int().nullable(),
    seats: z.number().int().nullable(), // purchased cap; null = no seat cap (esco)
    sites_held: z.number().int(), // active + suspended; draft never took one, decommissioned gave it back
})
    .strict();
// Shared list + detail field set.
const platformProjectBase = {
    row_version: RowVersionSchema,
    id: z.string().max(16),
    name: z.string().max(32), // the immutable code
    display_name: z.string().max(64).nullable(),
    type: ProjectTypeSchema,
    status: ProjectStatusSchema,
    // Commercial trial (orthogonal to type/status). The effective badge widens
    // status with the trial verdict frontend-side.
    is_trial: z.boolean(),
    trial_expires_at: z.string().datetime().nullable(),
    color_primary: z.string().regex(HEX_COLOR),
    color_on_primary: z.string().regex(HEX_COLOR),
    mother_company: z.string().max(80).nullable(),
    service_provider: z.string().max(80).nullable(),
    service_provider_address: z.string().max(300).nullable(),
    service_provider_support_email: z.string().max(320).nullable(),
    countries: z.array(z.string().length(2)), // whitelisted ISO alpha-2 codes
    default_language: LanguageSchema,
    has_invoicing: z.boolean(),
    has_field_force: z.boolean(),
    has_vehicles: z.boolean(),
    // The tiers the project includes, with their seat caps (null for esco). Tier
    // name + cap resolved via a join so the frontend renders without the catalogue.
    tiers: z.array(ProjectTierSchema),
    // Derived stats. member_count = user_project_roles rows; active_member_count_30d
    // = members whose users.last_login_at is within 30d; active_site_count = sites
    // at status='active' — NOT the per-tier `sites_held` above, which counts seats
    // held (active + suspended). Same table, different question.
    member_count: z.number().int(),
    active_member_count_30d: z.number().int(),
    active_site_count: z.number().int(),
    suspended_at: z.string().datetime().nullable(),
    terminated_at: z.string().datetime().nullable(),
    created_at: z.string().datetime(),
};
export const PlatformProjectListItemSchema = z.object(platformProjectBase).strict();
export const PlatformProjectsListResponseSchema = z
    .object({ items: z.array(PlatformProjectListItemSchema) })
    .strict();
// Detail adds the heavier identity + defaults fields the detail page edits.
export const PlatformProjectDetailSchema = z
    .object({
    ...platformProjectBase,
    description: z.string().nullable(),
    default_country_iso_code: z.string().length(2).nullable(),
    timezone_iana: z.string().max(64),
    week_start: z.number().int().min(1).max(7),
    default_currency: z.string().length(3),
    created_by: z.string().max(16).nullable(),
})
    .strict();
// The GLOBAL license-tier catalogue (seed-managed; ADR-055). The create wizard +
// the manage-licensing drawer read this and NEVER assume a fixed tier count.
export const LicenseTierSchema = z
    .object({
    row_version: RowVersionSchema,
    code: z.string().max(16),
    name: z.string().max(32),
    max_active_power_systems: z.number().int().nullable(),
    display_order: z.number().int(),
})
    .strict();
export const LicenseTiersResponseSchema = z.object({ items: z.array(LicenseTierSchema) }).strict();
// Offerable countries for the create/countries picker — the ISO code + the real DB
// default timezone, week start and currency (the wizard's pre-fill sources; ADR-030
// cascade · ADR-082 §3). Names resolve client-side via Intl, so only the codes
// cross the wire.
export const OfferableCountrySchema = z
    .object({
    iso_code: z.string().length(2),
    default_timezone_iana: z.string().max(64).nullable(),
    default_week_start: z.number().int().min(1).max(7),
    default_currency_code: z.string().length(3).nullable(),
})
    .strict();
export const OfferableCountriesResponseSchema = z
    .object({ items: z.array(OfferableCountrySchema) })
    .strict();
// POST result — the new project's id (the surface then routes to its detail page).
export const CreatedProjectResponseSchema = z.object({ id: z.string().max(16) }).strict();
// POST /api/platform/license-tiers result — the new tier's code (its stable key).
export const CreatedLicenseTierResponseSchema = z.object({ code: z.string().max(16) }).strict();
